/**
 * CrispChat - Live chat widget integration
 *
 * Provides real-time customer support via Crisp chat.
 * Configure CRISP_WEBSITE_ID in environment variables.
 *
 * Features:
 * - Auto-loads on public pages only
 * - Senior-friendly styling
 * - Accessible design
 * - Session persistence
 */

import { useEffect } from 'react';
import { useLocation } from 'wouter';

// Extend window type for Crisp
declare global {
  interface Window {
    $crisp: unknown[];
    CRISP_WEBSITE_ID: string;
  }
}

interface CrispChatProps {
  websiteId?: string;
}

// Pages where chat should NOT appear (authenticated/dashboard pages)
const EXCLUDED_PATHS = [
  '/patient/dashboard',
  '/caregiver/dashboard',
  '/login',
];

export function CrispChat({ websiteId }: CrispChatProps) {
  const [location] = useLocation();

  // Get website ID from props or environment
  const crispId = websiteId || import.meta.env.VITE_CRISP_WEBSITE_ID;

  useEffect(() => {
    // Don't load on excluded paths or if no ID configured
    if (!crispId || EXCLUDED_PATHS.some(path => location.startsWith(path))) {
      return;
    }

    // Check if Crisp is already loaded
    if (window.$crisp) {
      return;
    }

    // Initialize Crisp
    window.$crisp = [];
    window.CRISP_WEBSITE_ID = crispId;

    // Load Crisp script
    const script = document.createElement('script');
    script.src = 'https://client.crisp.chat/l.js';
    script.async = true;
    document.head.appendChild(script);

    // Configure Crisp after load
    script.onload = () => {
      if (window.$crisp) {
        // Set custom styling for senior-friendly appearance
        window.$crisp.push(['config', 'color:theme', ['teal']]);
        window.$crisp.push(['config', 'position:reverse', [false]]);

        // Set welcome message
        window.$crisp.push(['set', 'session:data', [[
          ['company', 'Life Alert AI'],
          ['plan', 'visitor']
        ]]]);
      }
    };

    // Cleanup
    return () => {
      // Note: Crisp doesn't cleanly unload, so we just leave it
      // This is fine as SPA navigations don't re-initialize
    };
  }, [crispId, location]);

  // Don't render anything - Crisp creates its own DOM elements
  return null;
}

/**
 * useCrisp - Hook for programmatic Crisp control
 */
export function useCrisp() {
  const openChat = () => {
    if (window.$crisp) {
      window.$crisp.push(['do', 'chat:open']);
    }
  };

  const closeChat = () => {
    if (window.$crisp) {
      window.$crisp.push(['do', 'chat:close']);
    }
  };

  const showChat = () => {
    if (window.$crisp) {
      window.$crisp.push(['do', 'chat:show']);
    }
  };

  const hideChat = () => {
    if (window.$crisp) {
      window.$crisp.push(['do', 'chat:hide']);
    }
  };

  const setUser = (email: string, name?: string) => {
    if (window.$crisp) {
      window.$crisp.push(['set', 'user:email', [email]]);
      if (name) {
        window.$crisp.push(['set', 'user:nickname', [name]]);
      }
    }
  };

  const sendMessage = (message: string) => {
    if (window.$crisp) {
      window.$crisp.push(['do', 'message:send', ['text', message]]);
    }
  };

  return {
    openChat,
    closeChat,
    showChat,
    hideChat,
    setUser,
    sendMessage,
  };
}

export default CrispChat;
